import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService, { Unit } from '../../services/unitService';
import unitConversionService from '../../services/unitConversionService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import '../Products/ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

// Max length constants for validation
const MAX_LENGTHS = {
  remarks: 200,
};

interface FieldErrors {
  fromUnitId?: string;
  toUnitId?: string;
  conversionFactor?: string;
  remarks?: string;
}

interface UnitConversionForm {
  fromUnitId: string;
  toUnitId: string;
  conversionFactor: string;
  remarks: string;
}

const UnitConversionCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [units, setUnits] = useState<Unit[]>([]);
  const [formData, setFormData] = useState<UnitConversionForm>({
    fromUnitId: '',
    toUnitId: '',
    conversionFactor: '',
    remarks: '',
  });

  useEffect(() => {
    loadUnits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUnits = async () => {
    try {
      const data = await unitService.getUnits(1, 100);
      if (Array.isArray(data)) {
        setUnits(data);
      }
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToLoadUnit')).message);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    // Clear field error when user starts typing
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // From unit — required
    if (!formData.fromUnitId) {
      errors.fromUnitId = t('unitConversion.formValidation.fromUnitRequired');
      isValid = false;
    }

    // To unit — required + must differ from the from unit
    if (!formData.toUnitId) {
      errors.toUnitId = t('unitConversion.formValidation.toUnitRequired');
      isValid = false;
    } else if (formData.toUnitId === formData.fromUnitId) {
      errors.toUnitId = t('unitConversion.formValidation.unitsMustDiffer');
      isValid = false;
    }

    // Conversion factor — required positive number
    const factor = parseFloat(formData.conversionFactor);
    if (!formData.conversionFactor.trim() || isNaN(factor)) {
      errors.conversionFactor = t('unitConversion.formValidation.factorRequired');
      isValid = false;
    } else if (factor <= 0) {
      errors.conversionFactor = t('unitConversion.formValidation.factorPositive');
      isValid = false;
    }

    // Remarks — optional, max length only
    if (formData.remarks && formData.remarks.length > MAX_LENGTHS.remarks) {
      errors.remarks = t('unitConversion.formValidation.remarksMaxLength');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await unitConversionService.createUnitConversion({
        fromUnitId: parseInt(formData.fromUnitId, 10),
        toUnitId: parseInt(formData.toUnitId, 10),
        conversionFactor: parseFloat(formData.conversionFactor),
        isProductSpecific: false,
        remarks: formData.remarks || undefined,
      });
      navigate('/products/unit-conversions', {
        state: { message: t('unitConversion.createSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unitConversion.failedToCreateConversion')).message);
      setLoading(false);
    }
  };

  const fromUnitName = units.find((u) => String(u.unitId) === formData.fromUnitId)?.unitName;
  const toUnitName = units.find((u) => String(u.unitId) === formData.toUnitId)?.unitName;

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/unit-conversions')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('unitConversion.newConversion')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/unit-conversions')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          {/* Form Section */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('unitConversion.createNewConversion')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  {/* From Unit */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.fromUnit')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-input ${fieldErrors.fromUnitId ? 'is-invalid' : ''}`}
                      name="fromUnitId"
                      value={formData.fromUnitId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unitConversion.selectUnit')}</option>
                      {units.map((unit) => (
                        <option key={unit.unitId} value={unit.unitId}>
                          {unit.unitName} ({unit.unitShortName})
                        </option>
                      ))}
                    </select>
                    {fieldErrors.fromUnitId && <small className="form-error">{fieldErrors.fromUnitId}</small>}
                    {!fieldErrors.fromUnitId && <small className="form-helper">{t('unitConversion.fromUnitHint')}</small>}
                  </div>

                  {/* To Unit */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.toUnit')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-input ${fieldErrors.toUnitId ? 'is-invalid' : ''}`}
                      name="toUnitId"
                      value={formData.toUnitId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unitConversion.selectUnit')}</option>
                      {units.map((unit) => (
                        <option key={unit.unitId} value={unit.unitId}>
                          {unit.unitName} ({unit.unitShortName})
                        </option>
                      ))}
                    </select>
                    {fieldErrors.toUnitId && <small className="form-error">{fieldErrors.toUnitId}</small>}
                    {!fieldErrors.toUnitId && <small className="form-helper">{t('unitConversion.toUnitHint')}</small>}
                  </div>

                  {/* Conversion Factor */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.conversionFactor')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.conversionFactor ? 'is-invalid' : ''}`}
                      name="conversionFactor"
                      value={formData.conversionFactor}
                      onChange={handleInputChange}
                      placeholder="1.0"
                      step="any"
                      min="0"
                    />
                    {fieldErrors.conversionFactor && <small className="form-error">{fieldErrors.conversionFactor}</small>}
                    {!fieldErrors.conversionFactor && (
                      <small className="form-helper">
                        {fromUnitName && toUnitName
                          ? t('unitConversion.factorExample', { from: fromUnitName, to: toUnitName })
                          : t('unitConversion.factorHint')}
                      </small>
                    )}
                  </div>

                  {/* Remarks */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.remarks')}
                      <span className="char-counter">{formData.remarks.length}/{MAX_LENGTHS.remarks}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.remarks ? 'is-invalid' : ''}`}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder={t('unitConversion.remarks')}
                      rows={4}
                      maxLength={MAX_LENGTHS.remarks}
                    />
                    {fieldErrors.remarks && <small className="form-error">{fieldErrors.remarks}</small>}
                    {!fieldErrors.remarks && <small className="form-helper">{t('unitConversion.remarksHint')}</small>}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('unitConversion.fromUnit')}</strong>
                  <p>{t('unitConversion.fromUnitHelp')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('unitConversion.toUnit')}</strong>
                  <p>{t('unitConversion.toUnitHelp')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('unitConversion.conversionFactor')}</strong>
                  <p>{t('unitConversion.factorHelp')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitConversionCreatePage;
